
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../lib/supabase";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSupabaseConnected, setIsSupabaseConnected] = useState(false);

  useEffect(() => {
    const checkSupabaseConnection = async () => {
      try {
        // より軽量な接続テスト
        const { data } = await supabase.auth.getUser();
        setIsSupabaseConnected(true);
        console.log("Supabase接続成功");
        
        // 既存のセッションがある場合はワーカーページへリダイレクト
        if (data?.user) {
          navigate("/admin/workers");
        }
      } catch (err) {
        console.error("Supabase接続エラー:", err);
        setIsSupabaseConnected(false);
        setError("データベース接続に問題があります。ページを再読み込みしてください。");
      }
    };
    
    checkSupabaseConnection();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // 入力値の検証
    if (!email.trim() || !password.trim()) {
      setError("メールアドレスとパスワードを入力してください。");
      setIsLoading(false);
      return;
    }

    if (!email.includes("@")) {
      setError("正しいメールアドレス形式で入力してください。");
      setIsLoading(false);
      return;
    }

    try {
      console.log("ログイン試行:", email);

      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password: password,
      });

      console.log("認証結果:", { user: data?.user ? "ユーザー情報あり" : "なし", error: authError });

      if (authError) {
        console.error("認証エラー:", authError);
        
        // エラーメッセージの分類
        switch (authError.message) {
          case "Invalid login credentials":
          case "Invalid credentials":
            setError("メールアドレスまたはパスワードが正しくありません。");
            break;
          case "Email not confirmed":
            setError("メールアドレスが確認されていません。メールをご確認ください。");
            break;
          case "Too many requests":
            setError("ログイン試行回数が多すぎます。しばらく待ってから再度お試しください。");
            break;
          default:
            if (authError.message.includes("fetch")) {
              setError("ネットワーク接続に問題があります。インターネット接続をご確認ください。");
            } else {
              setError("ログインに失敗しました。しばらく待ってから再度お試しください。");
            }
        }
        return;
      }

      if (data?.user) {
        console.log("ログイン成功");
        // 少し待ってからリダイレクト
        setTimeout(() => {
          navigate("/admin/workers");
        }, 500);
      } else {
        setError("ログイン情報の取得に失敗しました。");
      }
    } catch (err) {
      console.error("予期しないエラー:", err);
      setError("システムエラーが発生しました。しばらく待ってから再度お試しください。");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* ロゴ・タイトル */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 flex items-center justify-center bg-teal-500 rounded-2xl mx-auto mb-4 shadow-lg">
            <i className="ri-admin-line text-white text-3xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">管理者ログイン</h1>
          <p className="text-gray-600 text-sm">きらり保育園 管理システム</p>
        </div>

        {/* 接続状況表示 */}
        {!isSupabaseConnected && !error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 flex items-start gap-3">
            <i className="ri-loader-4-line animate-spin text-yellow-500 text-xl flex-shrink-0"></i>
            <p className="text-yellow-700 text-sm">システムに接続中...</p>
          </div>
        )}

        {/* ログインフォーム */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleLogin} className="space-y-5">
            {/* エラーメッセージ */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <i className="ri-error-warning-fill text-red-500 text-xl flex-shrink-0"></i>
                <div>
                  <p className="text-red-700 text-sm font-medium mb-1">エラー</p>
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* メールアドレス */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                メールアドレス
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="admin@example.com"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* パスワード */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                パスワード
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="••••••••"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  disabled={isLoading}
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
            </div>

            {/* ログインボタン */}
            <button
              type="submit"
              disabled={isLoading || !isSupabaseConnected}
              className="w-full bg-teal-500 text-white font-semibold py-3 px-4 rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  ログイン中...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-login-box-line"></i>
                  ログイン
                </span>
              )}
            </button>
          </form>

          {/* 新規登録リンク */}
          <div className="mt-6 pt-6 border-t border-gray-100 text-center">
            <p className="text-gray-600 text-sm mb-2">アカウントをお持ちでない方</p>
            <a
              href="/admin/register"
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm cursor-pointer"
            >
              <i className="ri-user-add-line"></i>
              新規管理者登録
            </a>
          </div>
        </div>

        {/* フッター */}
        <p className="text-center text-gray-500 text-xs mt-6">
          © きらり保育園箕面 管理システム
        </p>
      </div>
    </div>
  );
}
